import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { WebAuthn, webAuthnAuthenticatorQueryKeys } from '#api';
import { AuthenticationError, trackError } from '#api/errors.ts';
import { isPublicKeyCredential, isWebAuthnAbort, isWebAuthnCancellation } from '#api/webauthn.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { isWebAuthnSupported } from '#util/webauthn-support.ts';

type Props = {
  onSuccess: () => void;
};

export function RegisterWebAuthnForm({ onSuccess }: Props) {
  const [errorMessage, setErrorMessage] = useState<string>();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useAppLocation();
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | undefined>(undefined);

  const registerMutation = useMutation({
    mutationFn: async () => {
      const abortController = new AbortController();
      abortRef.current = abortController;

      // Step 1: Begin registration with the server
      const { warsToken, credentialCreation } = await WebAuthn.beginRegistration();

      // Step 2: Convert server options for the browser WebAuthn API
      const creationOptions = WebAuthn.prepareCreationOptions(credentialCreation);

      // Step 3: Prompt the user to create a credential via the browser.
      // The prompt stays open until the user acts on it, so tie it to this
      // component's lifetime: a prompt left open on a screen the user navigated
      // away from must not register a passkey later.
      const credential = await navigator.credentials.create({
        ...creationOptions,
        signal: abortController.signal,
      });
      if (!isPublicKeyCredential(credential)) {
        throw new Error('No credential was returned from the browser.');
      }

      // Step 4: Serialize and send the credential to the server, storing the authToken
      const serialized = WebAuthn.serializeCredential(credential);
      await auth.registerWebAuthn({
        warsToken,
        credentialCreationResponse: serialized,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: webAuthnAuthenticatorQueryKeys.lists() });
      onSuccess();
    },
    onError: (err) => {
      if (isWebAuthnAbort(err)) {
        // Aborted by unmount; there is no screen left to show an error on.
        return;
      }
      if (
        isWebAuthnCancellation(err) ||
        (err instanceof Error && err.message === 'No credential was returned from the browser.')
      ) {
        setErrorMessage('The request was cancelled or timed out. Please try again.');
      } else if (err instanceof AuthenticationError && err.code === 'ERR_2FA_RENEWAL_REQUIRED') {
        void navigate('/auth/mfa/renew', { state: { from: location } });
      } else {
        trackError(err);
        setErrorMessage('Could not register your passkey. Please try again.');
      }
    },
  });

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  return (
    <div>
      <p className="p-md mb-16">
        Register a passkey or hardware security key for quick, secure access to your account.
      </p>
      {errorMessage && <ErrorMessage className="mb-16">{errorMessage}</ErrorMessage>}
      <Button
        onClick={() => {
          setErrorMessage(undefined);
          registerMutation.mutate();
        }}
        disabled={registerMutation.isPending || !isWebAuthnSupported()}
      >
        {registerMutation.isPending ? 'Waiting for device...' : 'Register passkey'}
      </Button>
    </div>
  );
}
